import { lazy, type ComponentType, type LazyExoticComponent } from 'react';
import * as Sentry from '@sentry/react';

/**
 * Route-level `lazy()` that survives a deploy landing under an open tab (NAT-2174).
 *
 * Vite emits content-hashed chunks and drops the previous ones. A user holding the app open
 * across a deploy still has the old `index.html` in memory, so their next lazy route import
 * requests a filename that no longer exists on the CDN. The import rejects, nothing catches it,
 * and the page dies — 329 occurrences since 2025-10-02 (Sentry FE-1S), most visibly on
 * `/admin/client-orders/:code/edit`.
 *
 * A stale chunk is fixed by fetching the current `index.html`, so we reload once. This is safe
 * precisely because the failure happens on a route that never mounted: react-router has already
 * unmounted the previous screen by the time the import settles, so there is no live form state
 * left for a reload to discard.
 */

/** One-shot guard: without it, a chunk that is genuinely gone reloads forever. */
const RELOAD_GUARD_KEY = 'nat2174:chunk-reloaded';

/** How long to wait for `reload()` to actually take the page away before giving up on it. */
const RELOAD_GRACE_MS = 10_000;

/** Safari private mode and some embedded webviews throw on sessionStorage access. */
function guard(action: 'get' | 'set' | 'clear'): boolean {
  try {
    if (action === 'get') return window.sessionStorage.getItem(RELOAD_GUARD_KEY) !== null;
    if (action === 'set') window.sessionStorage.setItem(RELOAD_GUARD_KEY, '1');
    else window.sessionStorage.removeItem(RELOAD_GUARD_KEY);
    return false;
  } catch {
    // Storage unavailable — treat as "already reloaded" so we degrade to the error boundary
    // rather than risk a reload loop we cannot break out of.
    return action === 'get';
  }
}

/**
 * Browsers disagree on the wording, so match the shapes rather than one string:
 *  - Chrome/Edge: "Failed to fetch dynamically imported module: <url>"
 *  - Firefox:     "error loading dynamically imported module"
 *  - Safari:      "Importing a module script failed."
 */
export function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return (
    /failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /importing a module script failed/i.test(message)
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyRoute<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      const mod = await factory();
      // The app is serving current chunks again — re-arm the guard for the next deploy.
      guard('clear');
      return mod;
    } catch (error) {
      if (!isChunkLoadError(error)) throw error;

      // Already reloaded once this session and still missing: the chunk is not coming back.
      // Let it reach the error boundary instead of cycling — the rethrow is what reports it.
      if (guard('get')) throw error;

      // Record the recovery BEFORE reloading. The reload destroys this JS context, so without
      // this the successful case leaves no trace at all and we could never tell whether the fix
      // fires twice a month or two hundred times a day. It also matters for the one case the
      // message match cannot separate: WebKit reports "Importing a module script failed." for a
      // syntax error in a freshly deployed chunk exactly as it does for a missing one, so a
      // genuinely broken build would otherwise reload once and vanish silently.
      Sentry.captureException(error, {
        level: 'warning',
        tags: { chunk_recovery: 'reload' },
      });

      guard('set');

      // Bounded: never let telemetry delay the user's recovery.
      await Sentry.flush(2000).catch(() => undefined);
      window.location.reload();

      // Normally never settles — the reload takes over and Suspense keeps showing the route
      // fallback instead of flashing an error boundary on the way out. The timeout is the escape
      // hatch for when reload() does not take effect (a beforeunload handler, a locked-down
      // webview, an extension): rather than spin the fallback forever with no way out, surface
      // the original error to the boundary.
      return new Promise<never>((_resolve, reject) => {
        setTimeout(() => reject(error), RELOAD_GRACE_MS);
      });
    }
  });
}
